import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { env } from "../config/env";
import { getCachedPublicChrome, getCachedPublicTheme, getDefaultChrome, getPublicChrome, getPublicTheme } from "../data/cms";
import { getPublicSiteLinks } from "../utils/links";
import { trackCta } from "../analytics";
import { ButtonLink } from "./ButtonLink";
import { SocialLinks } from "./SocialShare";

const navItems = [
  { label: "About", href: "/about" },
  { label: "Classical Education", href: "/classical-education" },
  { label: "Academics", href: "/academics" },
  { label: "Admissions", href: "/admissions" },
  { label: "Parents", href: "/parents" },
  { label: "News", href: "/news" },
  { label: "Calendar", href: "/calendar" },
  { label: "Staff", href: "/staff" },
  { label: "Contact", href: "/contact" }
];

type LayoutProps = {
  children: React.ReactNode;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function hexToRgb(hex: string) {
  const normalized = hex.replace("#", "").trim();
  const full = normalized.length === 3
    ? normalized.split("").map((part) => `${part}${part}`).join("")
    : normalized;
  if (full.length !== 6) return null;
  const parsed = Number.parseInt(full, 16);
  if (!Number.isFinite(parsed)) return null;
  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  };
}

function rgbToHex(r: number, g: number, b: number) {
  return `#${[r, g, b].map((value) => clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0")).join("")}`;
}

function mixHex(base: string, target: string, weight: number) {
  const a = hexToRgb(base);
  const b = hexToRgb(target);
  if (!a || !b) return base;
  return rgbToHex(
    a.r + (b.r - a.r) * weight,
    a.g + (b.g - a.g) * weight,
    a.b + (b.b - a.b) * weight
  );
}

function applyThemeVariables(theme: Awaited<ReturnType<typeof getPublicTheme>>) {
  const root = document.documentElement;
  const inkRgb = hexToRgb(theme.inkColor) || { r: 22, g: 33, b: 58 };
  const goldRgb = hexToRgb(theme.accentColor) || { r: 200, g: 148, b: 46 };
  root.style.setProperty("--green", theme.primaryColor);
  root.style.setProperty("--gold", theme.accentColor);
  root.style.setProperty("--ink", theme.inkColor);
  root.style.setProperty("--ivory", theme.warmBackground);
  root.style.setProperty("--ink-rgb", `${inkRgb.r}, ${inkRgb.g}, ${inkRgb.b}`);
  root.style.setProperty("--gold-rgb", `${goldRgb.r}, ${goldRgb.g}, ${goldRgb.b}`);
  root.style.setProperty("--green-soft", mixHex(theme.primaryColor, "#ffffff", 0.84));
  root.style.setProperty("--gold-dark", mixHex(theme.accentColor, "#000000", 0.34));
  root.style.setProperty("--ink-soft", mixHex(theme.inkColor, "#ffffff", 0.14));
  root.style.setProperty("--text", mixHex(theme.inkColor, "#ffffff", 0.08));
  root.style.setProperty("--line", mixHex(theme.warmBackground, theme.inkColor, 0.12));
  if (theme.buttonStyle === "soft") root.classList.add("theme-soft-buttons");
  else root.classList.remove("theme-soft-buttons");
  if (theme.heroImageUrl) root.style.setProperty("--hero-image", `url("${theme.heroImageUrl}")`);
  else root.style.removeProperty("--hero-image");
}

export function Layout({ children }: LayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [chrome, setChrome] = useState(() => getCachedPublicChrome() ?? getDefaultChrome());
  const facebookUrl = chrome.showFacebookLink !== false ? chrome.facebookUrl || env.facebookUrl : "";
  const youtubeUrl = chrome.showYoutubeLink !== false ? chrome.youtubeUrl || "" : "";
  const { donationHref, parentPortalHref } = getPublicSiteLinks(chrome);
  const givingPageHref = "/giving";
  const utilityBarStyle = chrome.utilityBarBackground ? { background: chrome.utilityBarBackground } : undefined;
  const headerMainStyle = chrome.headerBackground ? { background: chrome.headerBackground } : undefined;
  const footerStyle = chrome.footerBackground ? { background: chrome.footerBackground } : undefined;

  useEffect(() => {
    let cancelled = false;
    const cachedTheme = getCachedPublicTheme();
    if (cachedTheme) applyThemeVariables(cachedTheme);
    getPublicTheme().then((theme) => {
      if (cancelled) return;
      applyThemeVariables(theme);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getPublicChrome().then((nextChrome) => {
      if (!cancelled) setChrome(nextChrome);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="site-shell">
      <header className="site-header">
        <div className="utility-bar" style={utilityBarStyle}>
          <a href={`tel:${env.schoolPhone}`}>{env.schoolPhone}</a>
          <span>{env.schoolAddress}</span>
          <a href={`mailto:${env.contactEmail}`}>{env.contactEmail}</a>
        </div>
        <div className="header-main" style={headerMainStyle}>
          <Link className="brand" to="/" aria-label="Zion Classical Academy home">
            <img className="brand-logo" src={chrome.logoUrl} alt="" width="54" height="54" />
            <span>
              <strong>Zion Classical Academy</strong>
              <small>Hastings, Nebraska</small>
            </span>
          </Link>

          <nav className="desktop-nav" aria-label="Main navigation">
            {navItems.map((item) => (
              <NavLink key={item.href} to={item.href}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="header-actions">
            <SocialLinks facebookUrl={facebookUrl} youtubeUrl={youtubeUrl} />
            <ButtonLink href={parentPortalHref} variant="outline" onClick={() => trackCta("parent_portal", "Header Parent Portal")}>
              Parent Portal
            </ButtonLink>
            <ButtonLink href={givingPageHref} variant="gold" onClick={() => trackCta("donate", "Header Donate/Give")}>
              Donate/Give
            </ButtonLink>
            <button className="menu-button" type="button" aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
              <span className="sr-only">Toggle navigation</span>
              <span aria-hidden="true">{menuOpen ? "Close" : "Menu"}</span>
            </button>
          </div>
        </div>

        {menuOpen ? (
          <nav className="mobile-nav" aria-label="Mobile navigation">
            {navItems.map((item) => (
              <NavLink key={item.href} to={item.href} onClick={() => setMenuOpen(false)}>
                {item.label}
              </NavLink>
            ))}
            <div className="mobile-actions">
              <SocialLinks facebookUrl={facebookUrl} youtubeUrl={youtubeUrl} />
              <ButtonLink href="/contact" variant="primary" onClick={() => trackCta("schedule_visit", "Mobile Schedule a Visit")}>
                Schedule a Visit
              </ButtonLink>
              <ButtonLink href={givingPageHref} variant="gold" onClick={() => trackCta("donate", "Mobile Donate/Give")}>
                Donate/Give
              </ButtonLink>
            </div>
          </nav>
        ) : null}
      </header>

      <main>{children}</main>

      <footer className="site-footer" style={footerStyle}>
        <div className="footer-grid">
          <div>
            <div className="footer-brand">
              <img src={chrome.logoUrl} alt="" width="64" height="64" />
              <h2>Zion Classical Academy</h2>
            </div>
            <p>{chrome.footerSummary}</p>
            <ButtonLink href="/contact" variant="gold" onClick={() => trackCta("schedule_visit", "Footer Schedule a Visit")}>
              Schedule a Visit
            </ButtonLink>
          </div>
          <div>
            <h3>Quick Links</h3>
            <ul>
              <li><Link to="/academics">Academics</Link></li>
              <li><Link to="/admissions">Admissions</Link></li>
              <li><Link to="/parents">Parents</Link></li>
              <li><Link to={givingPageHref}>Donate/Give</Link></li>
              <li><Link to="/christian-school-hastings-ne">Christian School in Hastings</Link></li>
              <li><Link to="/tuition-financial-aid">Tuition & Aid</Link></li>
            </ul>
          </div>
          <div>
            <h3>Connect</h3>
            <ul>
              <li><a href={`mailto:${env.contactEmail}`}>{env.contactEmail}</a></li>
              <li><a href={`tel:${env.schoolPhone}`}>{env.schoolPhone}</a></li>
              <li>{env.schoolAddress}</li>
            </ul>
            <SocialLinks facebookUrl={facebookUrl} youtubeUrl={youtubeUrl} />
          </div>
        </div>
        <p className="footer-bottom">&copy; {new Date().getFullYear()} {chrome.footerBottom}</p>
      </footer>
    </div>
  );
}
